/**
 * Editing and deleting a scheduled task (issue #546).
 *
 * "Kin's scheduled tasks" listed every recurring task with a pause toggle and
 * nothing else. A task set up wrong — wrong day, wrong time, a duplicate —
 * could only be paused, and it stayed in her list forever. Pro is a recurring
 * subscription whose whole value is recurring tasks; not being able to correct
 * one is a launch-blocking gap.
 *
 * The rules live here rather than inside TasksTab.tsx (16k lines) so they can
 * be tested against the code that actually ships.
 */

export type ScheduleMode = "weekly" | "interval";

export type EditableTask = {
  schedule_day: string | null;
  schedule_time: string | null;
  interval_days: number | null;
};

export const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

/**
 * Which shape of schedule this task uses.
 *
 * Mirrors `formatScheduleText` in TasksTab exactly: a null `schedule_day`
 * means the task repeats on an interval ("Every 3 days"), anything else is a
 * fixed weekday. Read that function before changing this — if the two
 * disagree, the row will describe a schedule the editor can't produce.
 */
export function scheduleModeFor(task: EditableTask): ScheduleMode {
  return task.schedule_day ? "weekly" : "interval";
}

/** "8:00" / "8:0" / "08:00:00" -> "08:00". Returns null if unparseable. */
export function normalizeTime(time: string | null | undefined): string | null {
  const match = /^(\d{1,2}):(\d{1,2})/.exec((time || "").trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/**
 * The next time a task on this schedule should fire, as an ISO string.
 *
 * `now` is injected so this is testable and so callers can't accidentally
 * depend on module-load time. Local time on purpose: the existing
 * `getNextRunAt` in TasksTab does the same, and matching it keeps a task's
 * fire time from shifting the moment she edits an unrelated field.
 */
export function nextRunAtFor(
  mode: ScheduleMode,
  opts: { day?: string | null; time: string; intervalDays?: number | null },
  now: Date
): string {
  const time = normalizeTime(opts.time) ?? "08:00";
  const [hours, minutes] = time.split(":").map(Number);
  const next = new Date(now.getTime());
  next.setHours(hours, minutes, 0, 0);

  if (mode === "interval") {
    const interval = Math.max(1, Math.floor(opts.intervalDays || 1));
    // Keep today if the time hasn't passed yet; otherwise the first fire is
    // one interval out. Without this, saving an edit at 9am for a 5pm daily
    // task would push the next run to tomorrow.
    if (next.getTime() <= now.getTime()) next.setDate(next.getDate() + interval);
    return next.toISOString();
  }

  const targetDow = WEEKDAYS.indexOf((opts.day || "").toLowerCase() as (typeof WEEKDAYS)[number]);
  if (targetDow < 0) return next.toISOString();
  let deltaDays = (targetDow - next.getDay() + 7) % 7;
  if (deltaDays === 0 && next.getTime() <= now.getTime()) deltaDays = 7;
  next.setDate(next.getDate() + deltaDays);
  return next.toISOString();
}

export type ScheduleUpdate = {
  schedule_day: string | null;
  schedule_time: string;
  interval_days: number | null;
  next_run_at: string;
  updated_at: string;
};

/**
 * The exact column set to write when she saves a schedule edit.
 *
 * `next_run_at` is recomputed here on purpose. The runner picks work up by
 * `next_run_at`, NOT by re-reading schedule_day/schedule_time — so changing
 * the schedule without recomputing it means the edit appears to save and the
 * task still fires at the old time. That is the failure this function exists
 * to make impossible.
 */
export function buildScheduleUpdate(
  mode: ScheduleMode,
  opts: { day?: string | null; time: string; intervalDays?: number | null },
  now: Date
): ScheduleUpdate {
  const time = normalizeTime(opts.time) ?? "08:00";
  const intervalDays =
    mode === "interval" ? Math.max(1, Math.floor(opts.intervalDays || 1)) : null;
  return {
    schedule_day: mode === "weekly" ? (opts.day || "").toLowerCase() || null : null,
    schedule_time: time,
    interval_days: intervalDays,
    next_run_at: nextRunAtFor(mode, { ...opts, time, intervalDays }, now),
    updated_at: now.toISOString(),
  };
}

/** Whether Save should be tappable, and what to say when it isn't. */
export function scheduleEditValidity(
  mode: ScheduleMode,
  opts: { day?: string | null; time: string; intervalDays?: number | null }
): { valid: boolean; hint: string | null } {
  if (!normalizeTime(opts.time)) {
    return { valid: false, hint: "Pick a time first." };
  }
  if (mode === "weekly" && !WEEKDAYS.includes((opts.day || "").toLowerCase() as never)) {
    return { valid: false, hint: "Pick a day first." };
  }
  if (mode === "interval" && !(Math.floor(opts.intervalDays || 0) >= 1)) {
    return { valid: false, hint: "How many days between runs?" };
  }
  return { valid: true, hint: null };
}

/**
 * Confirms a write actually landed.
 *
 * ⚠️ This is the whole reason writing here is riskier than it looks. Under
 * RLS, an UPDATE or DELETE with no matching policy touches zero rows and
 * returns NO ERROR — PostgREST answers 200 with an empty array, which is
 * indistinguishable from success. The change would show on her screen
 * optimistically and be gone on the next refresh, which is precisely the
 * silent failure #537/#545/#548 were about.
 *
 * So callers ask PostgREST to return the affected ids (`.select("id")`) and
 * run them through here. No rows back means it did not happen, and she gets
 * told.
 *
 * One helper on purpose: `runTaskNow` grew its own inline copy of this rule
 * (`if (!data || data.length === 0) throw`) at the same time this shipped, and
 * three hand-synced copies of one rule is this codebase's most common defect
 * shape (#361, #470, #515). Every proof-of-write check in TasksTab goes
 * through this function.
 */
export function assertWriteLanded(rows: unknown, label = "task"): void {
  if (Array.isArray(rows) && rows.length > 0) return;
  throw new Error(
    `Write affected no rows — the ${label} is unchanged. Most likely a missing RLS policy.`
  );
}

/**
 * #1612 — the cadence a row with no `schedule_day` ACTUALLY runs on.
 *
 * run-scheduled-tasks' `calculateNextRunAtLocal` uses `interval_days` only when
 * it is a positive number, and otherwise schedules +7 days. The task-row label
 * used to fall back to 1 ("Daily"), so a reminder that fired weekly told her
 * it fired every day. The schedule editor already seeded 7. All three read
 * this now.
 */
export const RUNNER_FALLBACK_INTERVAL_DAYS = 7;

export function effectiveIntervalDays(intervalDays: number | null | undefined): number {
  return typeof intervalDays === "number" && intervalDays > 0 ? intervalDays : RUNNER_FALLBACK_INTERVAL_DAYS;
}
